import React, { useEffect, useRef } from 'react';
import { InfoBox } from './Components';
import HeatingEnergyByDevice from './charts/HeatingEnergyByDevice';
import TotalHeatingEnergy from './charts/TotalHeatingEnergy';
import TotalConsumption from './charts/TotalConsumption';
import HeaterTypesPieChart from './charts/HeaterTypesPieChart';
import SpotPriceComparison from './charts/SpotPriceComparison';
import Co2Chart from './charts/Co2Chart';
import HumidityChart from './charts/HumidityChart';
import TemperatureChart from './charts/TemperatureChart';
import PVMetrics from './charts/PVMetrics';


// At the beginning of your MonthlyStatus component:
const MonthlyStatus = ({ 
  data = null, 
  currentMonth, 
  onPreviousMonth, 
  onNextMonth, 
  errors,
  isLoading,
  isActualCurrentMonth,
  hasMoreHistory,
  hasNextMonthData
}) => {
  // Add this useEffect to log and debug when data changes
  useEffect(() => {
    console.log("MonthlyStatus received data update:", data);
  }, [data]);
  
  // Ensure we don't lose our data during renders
  // Store the last valid data in a ref
  const lastValidDataRef = useRef(data);
  
  // Update the ref when we get valid data
  useEffect(() => {
    if (data) {
      lastValidDataRef.current = data;
    }
  }, [data]);
  
  // Use the last valid data or the current data
  const displayData = data || lastValidDataRef.current;
  
  // Log what we're actually rendering with
  console.log("Monthly DISPLAY DATA", displayData);
  
  // Format the month for display in Finnish
  const formatDisplayMonth = (yearMonth) => {
    if (!yearMonth) return '';
    const [year, month] = yearMonth.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    
    // Format as "Month YYYY" in Finnish (e.g., "Helmikuu 2025")
    return date.toLocaleDateString('fi-FI', { month: 'long', year: 'numeric' });
  };

  const formatAreaName = (name) => 
    name.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

  const recommendedTemps = [
    { label: "Makuuhuoneet", value: "18–20 ºC" },  
    { label: "Oleskelutilat", value: "20–21 ºC" },
    { label: "Kylpyhuoneet", value: "22–23 ºC" },
    { label: "Varastot", value: "12 ºC" },
    { label: "Autotallit", value: "5 ºC" },
  ];
  const recommendedRH = [
    { label: "Oleskelutilat", value: "> 35 %" },
    { label: "Varastot", value: "< 65 %" }
  ];

  {/*if (isLoading) {
    return <div>Loading monthly data...</div>;
  } */}

  if (errors) {
    return <div className="error">{errors}</div>;
  }

  if (!displayData) {
    return <div>No monthly data available</div>;
  }

  return (
    <>
<div className="center-header">
      <h2>{formatDisplayMonth(displayData.energycontrol.metadata.period)}</h2>
    </div>  
    <div className="center-text">
       {hasMoreHistory ? (
          <button 
            onClick={onPreviousMonth}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            &larr; Edellinen kuukausi
          </button>
        ) : null}
        {" "}
        {!isActualCurrentMonth && hasNextMonthData ? (
          <button 
            onClick={onNextMonth}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Seuraava kuukausi &rarr;
          </button>
        ) : null}
    </div>
    
{/*    
    <div className="container mx-auto px-4">
      
      <div className="flex items-center justify-between mb-6">
      
        {hasMoreHistory ? (
          <button 
            onClick={onPreviousMonth}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            &larr; Edellinen kuukausi
          </button>
        ) : null}
        <h2 className="text-2xl font-bold">{formatDisplayMonth(currentMonth)}</h2>
      
        {!isActualCurrentMonth ? (
          <button 
            onClick={onNextMonth}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Seuraava kuukausi &rarr;
          </button>
        ) : null}

    
        

      </div>
*/}  
      {/* Grid layout that stacks on mobile and shows 2 columns on larger screens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Energy Control Section */}
            {displayData.energycontrol && (
              <>
              {/* Each chart container with responsive height */}
              <div className="bg-white rounded-lg shadow">
                <div className="p-4">
                  <h3 className="text-xl font-semibold mb-4">Hintavertailu</h3>
                  <div className="h-[300px] md:h-[400px]">
                    <SpotPriceComparison data={displayData.energycontrol} />
                  </div>
                </div>
              </div>

                  {/* PV Metrics - only show if pv_metrics exists */}
              {displayData.energycontrol.metadata?.pv_metrics && (
                <div className="bg-white rounded-lg shadow">
                  <div className="p-4">
                    <h3 className="text-xl font-semibold mb-4">Aurinkosähkön yhteenveto - BETA</h3>
                    <div className="h-[300px] md:h-[400px]">
                      <PVMetrics data={displayData.energycontrol} />
                    </div>
                  </div>
                </div>
              )}
              
              <div className="bg-white rounded-lg shadow">
                <div className="p-4">
                  <h3 className="text-xl font-semibold mb-4">Lämmitysenergian kulutus</h3>
                  <div className="h-[300px] md:h-[400px]">
                    <TotalHeatingEnergy data={displayData.energycontrol} />
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow">
                <div className="p-4">
                  <h4 className="text-xl font-semibold mb-4">Kulutus lämmityslaitteittain</h4>
                  <div className="h-[300px] md:h-[400px]">
                    <HeatingEnergyByDevice data={displayData.energycontrol} />
                  </div>
                </div>
              </div>
      
              <div className="bg-white rounded-lg shadow lg:col-span-2">
                <div className="p-4">
                  <h4 className="text-xl font-semibold mb-4">Jakauma lämmityslaitetyypeittäin</h4>
                  <div className="h-[300px] md:h-[400px]">
                    <HeaterTypesPieChart data={displayData.energycontrol} />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow">
                <div className="p-4">
                  <h3 className="text-xl font-semibold mb-4">Muu energiankulutus</h3>
                  <div className="h-[300px] md:h-[400px]">
                    <TotalConsumption data={displayData.energycontrol} />
                  </div>
                </div>
              </div>
              </>
            )}    
        
  
        {/* Air Control Section */}
        {displayData.aircontrol && (
          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 gap-6">
              {/* CO2 Chart */}
              <div className="bg-white rounded-lg shadow">
                <div className="p-4">
                  <h3 className="text-xl font-semibold mb-4">CO2-tasot</h3>
                  <Co2Chart data={displayData.aircontrol} />
                </div>
              </div>

              {/* Temperature Charts */}
              <div>
                <h3>Lämpötilat</h3>
                <InfoBox title="Kodin tilojen suositeltavat lämpötilat vedottomissa olosuhteissa (Motiva)" dataPoints={recommendedTemps} />
              </div>
              
              {Object.keys(displayData.aircontrol.temperature || {}).map(area => (
                <div key={`temp-${area}`} className="bg-white rounded-lg shadow">
                  <div className="p-4">
                    <h4 className="text-xl font-semibold mb-4">
                      {formatAreaName(area)}
                    </h4>
                    <TemperatureChart 
                      data={displayData.aircontrol}
                      area={area}
                    />
                  </div>
                </div>
              ))}

              {/* Humidity Charts */}
              <div>
                <h3>Kosteus</h3>
                <InfoBox title="Kodin tilojen suositeltavat kosteusprosentit: " dataPoints={recommendedRH} />
              </div>
              
              {Object.keys(displayData.aircontrol.humidity || {}).map(area => (
                <div key={`humidity-${area}`} className="bg-white rounded-lg shadow">
                  <div className="p-4">
                    <h4 className="text-xl font-semibold mb-4">
                      {formatAreaName(area)}
                    </h4>
                    <HumidityChart 
                      data={displayData.aircontrol}
                      area={area}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
    
      </div>
   
    </>
  );
};

export default MonthlyStatus;
